// Field types and roles of slice 4. The server validates and normalises every
// value (backend/src/FieldTypes.php); these helpers only give early feedback
// and display values. Keep the rules in step with the server's.

export const FIELD_TYPES = [
  'text', 'textarea', 'number', 'date', 'select', 'radio', 'checkbox',
  'yesno', 'tags', 'link', 'rating', 'money', 'measure', 'identifier', 'image', 'vocab',
];

/** Types holding media ids: a saved field never changes to or from them (the server refuses). */
export const MEDIA_TYPES = ['image'];
export const MAX_IMAGES = 20;
export const MAX_IMAGE_BYTES = 15 * 1024 * 1024;

export const IDENTIFIER_SCHEMES = ['ean', 'isbn', 'tmdb', 'bgg', 'igdb', 'imdb', 'olid', 'gbooks', 'bnf', 'other'];

/** Role => the field types that may play it. */
export const ROLES = {
  title: ['text'],
  subtitle: ['text'],
  creators: ['tags', 'text'],
  year: ['number', 'date', 'text'],
  identifier: ['identifier'],
  series: ['text', 'select'],
  series_number: ['number', 'text'],
  description: ['textarea', 'text'],
  cover: ['image'],
};

/** Roles several fields of one type may share. */
export const MULTIPLE_ROLES = ['identifier'];

export const DEFAULT_CURRENCY = 'EUR';
export const COMMON_UNITS = ['min', 'h', 'cm', 'm', 'g', 'kg', 'l', 'ml', 'p.'];

export const rolesFor = (type) => Object.keys(ROLES).filter(role => ROLES[role].includes(type));

/** Options of the new types, filled with their defaults (the server does the same). */
export const withTypeDefaults = (field) => {
  const out = { ...field };
  if (out.type !== 'rating') delete out.max;
  if (out.type !== 'money') delete out.currency;
  if (out.type !== 'measure') delete out.units;
  if (out.type !== 'identifier') delete out.scheme;
  if (out.type !== 'vocab') delete out.vocabulary;
  if (out.type === 'vocab') out.vocabulary = out.vocabulary || 'tmdb_movie_genres';
  if (out.type === 'rating') out.max = Number(out.max) === 5 ? 5 : 10;
  if (out.type === 'money') out.currency = (out.currency || DEFAULT_CURRENCY).toUpperCase();
  if (out.type === 'measure') out.units = Array.isArray(out.units) ? out.units : [];
  if (out.type === 'identifier') out.scheme = out.scheme || 'other';
  if (out.role && !(ROLES[out.role] || []).includes(out.type)) delete out.role;
  if (!out.role) delete out.role;
  return out;
};

// --- Identifiers ---

const gtinCheckDigit = (body) => {
  const padded = body.padStart(12, '0');
  let sum = 0;
  for (let i = 0; i < 12; i++) sum += Number(padded[i]) * (i % 2 === 0 ? 1 : 3);
  return (10 - (sum % 10)) % 10;
};

const gtinValid = (digits) => gtinCheckDigit(digits.slice(0, -1)) === Number(digits.slice(-1));

const NOID = '0123456789bcdfghjkmnpqrstvwxz';

/** A BnF record: the cb… part of its ark, lower-cased, its check character verified (as the server). */
const bnfRecord = (value) => {
  let id = String(value).toLowerCase();
  const ark = id.match(/(?:^|\/)ark:\/12148\/(cb[0-9a-z]+)(?:[/?#.].*)?$/);
  if (ark) id = ark[1];
  if (!/^cb\d{8,9}[0-9bcdfghjkmnpqrstvwxz]$/.test(id)) return null;
  let sum = 0;
  for (let i = 0; i < id.length - 1; i++) sum += (i + 1) * Math.max(0, NOID.indexOf(id[i]));
  return NOID[sum % 29] === id[id.length - 1] ? id : null;
};

/** The ISBN-10 of a 978 ISBN-13 (X check character included), or null: a 979 has none. Shown, never stored. */
export const isbn10 = (value) => {
  const isbn = isbn13(value ?? '');
  if (!isbn || !isbn.startsWith('978')) return null;
  const nine = isbn.slice(3, 12);
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += Number(nine[i]) * (10 - i);
  const check = (11 - (sum % 11)) % 11;
  return nine + (check === 10 ? 'X' : String(check));
};

/** ISBN-10 or -13 (dashes, spaces, "ISBN" prefix allowed) as 13 digits, or null. */
export const isbn13 = (value) => {
  let text = String(value).trim().toUpperCase().replace(/^ISBN(-1[03])?:?\s*/, '').replace(/[\s-]/g, '');
  if (/^\d{9}[\dX]$/.test(text)) {
    let sum = 0;
    for (let i = 0; i < 10; i++) sum += (text[i] === 'X' ? 10 : Number(text[i])) * (10 - i);
    if (sum % 11 !== 0) return null;
    const twelve = '978' + text.slice(0, 9);
    return twelve + gtinCheckDigit(twelve);
  }
  return /^97[89]\d{10}$/.test(text) && gtinValid(text) ? text : null;
};

/** The normalised identifier, or null when the value is not one of the scheme. */
export const normalizeIdentifier = (scheme, value) => {
  const text = String(value ?? '').trim();
  switch (scheme) {
    case 'ean': {
      const digits = text.replace(/[\s-]/g, '');
      if (!/^(\d{8}|\d{12}|\d{13})$/.test(digits) || !gtinValid(digits)) return null;
      return digits.length === 12 ? '0' + digits : digits;   // UPC-A is stored as EAN-13
    }
    case 'isbn':
      return isbn13(text);
    case 'tmdb':
    case 'bgg':
    case 'igdb': {
      if (!/^\d{1,15}$/.test(text)) return null;
      const id = text.replace(/^0+/, '');
      return id && id.length <= 12 ? id : null;
    }
    case 'imdb': {
      const id = text.toLowerCase();
      return /^tt\d{7,10}$/.test(id) ? id : null;
    }
    case 'olid': {
      const id = text.replace(/^(https?:\/\/openlibrary\.org)?\/?books\//i, '').toUpperCase();
      return /^OL[1-9]\d{0,11}M$/.test(id) ? id : null;
    }
    case 'gbooks':
      return /^[A-Za-z0-9_-]{12}$/.test(text) ? text : null;
    case 'bnf':
      return bnfRecord(text);
    default: {
      const id = text.replace(/\s+/g, ' ');
      return id && id.length <= 100 ? id : null;
    }
  }
};

/**
 * A scanned or typed code, read as the server reads it (FieldTypes::readCode,
 * phase 4 §2): an ISBN (10 or 13, as 13 digits), else an EAN-13, UPC-A (as
 * EAN-13 with a leading 0) or EAN-8 with a valid check digit; a 2- or
 * 5-digit add-on after an EAN-13 or UPC-A is dropped. {scheme, value} or null.
 */
export const readCode = (value) => {
  if (typeof value !== 'string' || value.length > 40) return null;
  const compact = value.trim().toUpperCase().replace(/[\s-]/g, '');
  const isbn = isbn13(compact);
  if (isbn) return { scheme: 'isbn', value: isbn };
  if (!/^\d+$/.test(compact)) return null;
  const candidates = [compact];
  if (compact.length === 15 || compact.length === 18) candidates.push(compact.slice(0, 13));
  if (compact.length === 14 || compact.length === 17) candidates.push(compact.slice(0, 12));
  for (const digits of candidates) {
    const asIsbn = isbn13(digits);
    if (asIsbn) return { scheme: 'isbn', value: asIsbn };
    const ean = normalizeIdentifier('ean', digits);
    if (ean) return { scheme: 'ean', value: ean };
  }
  return null;
};

/** An http(s) address the server will accept (it checks the same way). */
export const isValidLink = (value) => {
  const url = String(value ?? '').trim();
  return url.length <= 2000 && !/\s/.test(url) && /^https?:\/\/([^/?#@]*@)?[^/?#:@.][^/?#:@]*(:\d{1,5})?([/?#].*)?$/i.test(url);
};

export const linkDomain = (url) => {
  const match = /^https?:\/\/(?:[^/?#@]*@)?([^/?#:]+)/i.exec(String(url));
  return match ? match[1].replace(/^www\./i, '') : String(url);
};

/** Tags as a list, whatever was stored or typed (a string is split like the server does). */
export const asTags = (value) => {
  if (Array.isArray(value)) return value.map(String);
  if (value === null || value === undefined || value === '') return [];
  return String(value).split(/[;,|\r\n]+/).map(s => s.trim()).filter(Boolean);
};

const isEmpty = (value) => value === null || value === undefined || value === ''
  || (Array.isArray(value) && value.length === 0);

/** A number for display, or null when the stored value is not one (shown as text then). */
const asNumber = (value) => {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null;
  const n = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

/** A value as plain text for the list (lang: the UI language, for numbers and money). */
export const formatValue = (field, value, lang) => {
  if (isEmpty(value)) return null;
  const plain = () => {
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return Object.values(value).filter(v => v !== null && v !== '').join(' ');
    return String(value);
  };
  switch (field?.type) {
    case 'money': {
      const raw = value && typeof value === 'object' ? value.amount : value;
      const currency = (value && typeof value === 'object' && value.currency) || field.currency || DEFAULT_CURRENCY;
      const amount = asNumber(raw);
      if (amount === null) return plain();     // stored before the field was money: as typed
      try {
        return new Intl.NumberFormat(lang, { style: 'currency', currency }).format(amount);
      } catch (e) {
        return `${amount}\u00a0${currency}`;
      }
    }
    case 'measure': {
      const raw = value && typeof value === 'object' ? value.value : value;
      const number = asNumber(raw);
      if (number === null) return plain();
      const unit = (value && typeof value === 'object' && value.unit) || (field.units || [])[0] || '';
      return `${new Intl.NumberFormat(lang).format(number)}\u00a0${unit}`.trim();
    }
    case 'image':
      return Array.isArray(value) ? String(value.length) : plain();
    case 'rating': {
      const number = asNumber(value);
      if (number === null) return plain();
      return `★\u00a0${new Intl.NumberFormat(lang).format(number)}/${Number(field.max) === 5 ? 5 : 10}`;
    }
    default:
      return plain();
  }
};

/**
 * A translated message for an API error carrying a field code (invalid_value,
 * invalid_role, invalid_field), followed by the server's reason when it gives
 * one (in English: "expected a rating from 0 to 5").
 */
export const fieldErrorMessage = (t, error, fields = []) => {
  if (['invalid_image', 'image_too_large'].includes(error?.response?.data?.code)) {
    return t(`mediaErrors.${error.response.data.code}`);
  }
  const data = error?.response?.data || {};
  if (!['invalid_value', 'invalid_role', 'invalid_field', 'preset_field_locked'].includes(data.code)) return null;
  const field = fields.find(f => f.key === data.field);
  const message = t(`fieldErrors.${data.code}`, { field: field?.name || data.field || '' });
  return data.reason ? `${message} (${data.reason})` : message;
};

/** Equal values for "is it unsaved?": empty forms of a value ('' / null / []) are equal, and keys' order does not matter. */
export const sameValue = (a, b) => {
  const empty = (v) => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
  if (empty(a) || empty(b)) return empty(a) && empty(b);
  const canonical = (v) => (v && typeof v === 'object' && !Array.isArray(v)
    ? Object.keys(v).sort().reduce((out, k) => ({ ...out, [k]: canonical(v[k]) }), {})
    : Array.isArray(v) ? v.map(canonical) : v);
  return JSON.stringify(canonical(a)) === JSON.stringify(canonical(b));
};

// --- Show as filter (phase 3 §3.1) ---

/** Types the list can filter on: as a checklist, or as a range. */
export const CHECKLIST_TYPES = ['select', 'radio', 'checkbox', 'vocab', 'yesno', 'tags'];
export const RANGE_TYPES = ['number', 'rating', 'money', 'measure', 'date'];
export const canFilter = (field) => !!field && (CHECKLIST_TYPES.includes(field.type) || RANGE_TYPES.includes(field.type))
  && field.role !== 'year';

/**
 * Whether a field is among the list's filters (the server's
 * Filters::shownByDefault): its own `filter`, else checklists of options,
 * codes and yes/no on, a tags field on when it names the creators or shows
 * in the list, ranges off. The year field is always the Year filter.
 */
export const showsAsFilter = (field) => {
  if (!canFilter(field) || field.unused) return false;
  if (typeof field.filter === 'boolean') return field.filter;
  if (field.type === 'tags') return field.role === 'creators' || field.in_view !== false;
  return CHECKLIST_TYPES.includes(field.type);
};

/** The field with "Show as filter" set: the property dropped when the choice is the type's default (absent = default). */
export const withFilter = (field, show) => {
  const { filter, ...rest } = field;
  return showsAsFilter(rest) === show ? rest : { ...rest, filter: show };
};

